/**
 * Typed collection references, over the engine's path strings.
 *
 * Four collections and no more. The admin app curates **the catalog** and **the global
 * library**, pre-renders the global library's pictures, and holds **the issues tracker** —
 * it has no reason to know an author's path exists, and the rules would refuse it if it
 * asked.
 *
 * The converters are the same defensive readers everything else uses (`normalizeMedium`,
 * `normalizePrimitive`), so a malformed row is refused identically here and in the Studio.
 */

import { MEDIUMS, PRIMITIVES, VARIANT_INSTANCES } from '@atlas-nook/story';
import { normalizePrimitive, normalizeVariantInstance } from '@atlas-nook/story';
import type { Primitive, VariantInstance } from '@atlas-nook/story';
import { normalizeMedium } from '@atlas-nook/mediums';
import type { Medium } from '@atlas-nook/mediums';
import { collection, doc } from 'firebase/firestore';
import type {
  CollectionReference,
  DocumentReference,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
} from 'firebase/firestore';

import { db } from './firestore.js';
import { ISSUES, normalizeIssue } from './issues.js';
import type { Issue } from './issues.js';

/**
 * Read converters, and only read.
 *
 * `toFirestore` throws on purpose: every write in this app goes through `writes.ts`, which
 * is the only module that knows what shape the rules accept and which fields the forms own.
 * A converter that also wrote would make that bypassable by accident.
 */
function readOnly<T>(read: (id: string, data: unknown) => T): FirestoreDataConverter<T> {
  return {
    toFirestore(): never {
      throw new Error('converters are read-only: write through src/data/writes.ts');
    },
    fromFirestore(snapshot: QueryDocumentSnapshot): T {
      return read(snapshot.id, snapshot.data());
    },
  };
}

export const mediumConverter: FirestoreDataConverter<Medium> = readOnly((id, data) =>
  normalizeMedium({ ...(data as object), id }),
);
export const primitiveConverter: FirestoreDataConverter<Primitive> = readOnly(normalizePrimitive);
export const variantInstanceConverter: FirestoreDataConverter<VariantInstance> =
  readOnly(normalizeVariantInstance);
export const issueConverter: FirestoreDataConverter<Issue> = readOnly(normalizeIssue);

/** The catalog. Admin-write, and admin-read for the unpublished rows nobody else may see. */
export function mediums(): CollectionReference<Medium> {
  return collection(db(), MEDIUMS).withConverter(mediumConverter);
}

export function medium(id: string): DocumentReference<Medium> {
  return doc(mediums(), id);
}

/** The curated global library: `primitives`. World-readable, admin-written. */
export function globalPrimitives(): CollectionReference<Primitive> {
  return collection(db(), PRIMITIVES).withConverter(primitiveConverter);
}

export function globalPrimitive(id: string): DocumentReference<Primitive> {
  return doc(globalPrimitives(), id);
}

/** The shared, content-addressed render cache. Read by id only — see `queries.ts`. */
export function variantInstances(): CollectionReference<VariantInstance> {
  return collection(db(), VARIANT_INSTANCES).withConverter(variantInstanceConverter);
}

export function variantInstance(id: string): DocumentReference<VariantInstance> {
  return doc(variantInstances(), id);
}

/**
 * The issues tracker (`docs/dev/admin/02`). Admin-only on both sides — no family reads it,
 * and the CLI the code agent runs is the only other thing that writes it.
 */
export function issues(): CollectionReference<Issue> {
  return collection(db(), ISSUES).withConverter(issueConverter);
}

export function issue(id: string): DocumentReference<Issue> {
  return doc(issues(), id);
}
